import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { formatINR } from "@/lib/api";
import { KPIS, KPI_COLORS, percent, monthLabel } from "./helpers";
import { MeasuredChart, chartTooltipProps } from "./MeasuredChart";

const shortName = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).join(" ");
const compact = (v) => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`;

export const PerformanceOverview = ({ items, month, kpi, setKpi }) => {
  const totals = useMemo(() => Object.fromEntries(KPIS.map((k) => {
    const target = items.reduce((sum, p) => sum + (p[`${k}_target`] || 0), 0);
    const achieved = items.reduce((sum, p) => sum + (p[`${k}_achieved`] || 0), 0);
    return [k, { target, achieved, percent: percent(achieved, target) }];
  })), [items]);
  const chartData = items.map((p) => ({ name: shortName(p.employee_name), Target: p[`${kpi}_target`] || 0, Achieved: p[`${kpi}_achieved`] || 0 }));
  return <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="perf-team-summary">
      {KPIS.map((k) => <div key={k} className="card-flat min-w-0" data-testid={`perf-team-${k}`}>
        <div className="flex justify-between items-center gap-2"><h2 className="text-sm font-semibold capitalize">Team {k}</h2><span className="text-xs font-semibold" style={{ color: KPI_COLORS[k].color }}>{totals[k].percent == null ? "No target" : `${totals[k].percent}%`}</span></div>
        <p className="font-heading text-2xl font-bold mt-3 break-all" style={{ color: KPI_COLORS[k].color }}>{formatINR(totals[k].achieved)}</p><p className="text-xs text-stone-500 mt-1">of {formatINR(totals[k].target)} target</p>
      </div>)}
    </div>
    <section className="py-5 border-y border-stone-300" data-testid="perf-chart-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-heading font-semibold text-base md:text-lg" data-testid="perf-chart-title">Target vs Achieved · {monthLabel(month)}</h2>
        <div className="flex flex-wrap rounded-md border border-stone-200 overflow-hidden bg-white" role="group" aria-label="Comparison metric">
          {KPIS.map((k) => <button key={k} onClick={() => setKpi(k)} data-testid={`kpi-toggle-${k}`} aria-pressed={kpi === k} className="px-3 py-2 text-xs font-semibold capitalize transition-colors" style={kpi === k ? { background: KPI_COLORS[k].color, color: "white" } : { color: "#57534E" }}>{k}</button>)}
        </div>
      </div>
      <MeasuredChart height={280} testId="monthly-measured-chart">
        {(width, height) => <BarChart width={width} height={height} data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} minTickGap={25} />
            <YAxis tickFormatter={compact} tick={{ fontSize: 10 }} width={58} />
            <Tooltip {...chartTooltipProps} formatter={(v) => formatINR(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Target" fill="#D6D3D1" radius={[4, 4, 0, 0]} maxBarSize={36} /><Bar dataKey="Achieved" fill={KPI_COLORS[kpi].color} radius={[4, 4, 0, 0]} maxBarSize={36} />
          </BarChart>}
      </MeasuredChart>
    </section>
  </>;
};